"use client";

import Link from "next/link";
import Image from "next/image";
import { type MouseEventHandler, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { getYouTubeEmbedUrl, getYouTubeThumbnail } from "@/lib/utils/youtube";
import { getProxiedImageUrl } from "@/lib/utils/image-proxy";
import { getVideoPlaybackUrl } from "@/lib/utils/media-url";
import type { Media } from "@/types";
import { usePlayerControls } from "@/lib/hooks";
import { isSafeLinkHref } from "@/lib/security/safe-href";

import GalleryRasterImage from "@/components/media/GalleryRasterImage";
import GlassPlayButton from "@/components/ui/glass-play-button";
import { ga4VideoPlay } from "@/lib/analytics/ga4-ecommerce";

type Props = {
	data: Media | null;
	altHref?: string;
	className?: string;
	childClassName?: string;
	thumbnail?: string;
	disablePlayback?: boolean;
	altText?: string;
	priority?: boolean; // For LCP images above the fold
	/**
	 * Fixed-height carousels (e.g. category cards): outer wrapper uses full tile height (`h-40`).
	 * Raster images always use `object-cover` so non-16:9 uploads fill the frame (center crop).
	 */
	fillTileHeight?: boolean;
	/** When this slide fails to load, try these gallery raster URLs (e.g. next image). */
	fallbackRasterSources?: string[];
	/** Service detail carousel — show full-resolution `url` instead of listing `displayUrl`. */
	preferOriginalImage?: boolean;
};

export default function MediaCard({
	data,
	altHref,
	className,
	childClassName,
	thumbnail,
	disablePlayback,
	altText = "Media content",
	priority = false,
	fillTileHeight = false,
	fallbackRasterSources,
	preferOriginalImage = false,
}: Props) {

	if (data === null || !data.url) {
		return <div>No media available</div>;
	}

	// Handle YouTube videos
	if (data.type === "VIDEO" && data.source === "youtube" && data.youtubeId) {
		return (
			<YouTubePlayer
				youtubeId={data.youtubeId}
				className={className}
				disablePlayback={disablePlayback}
				altText={altText}
				priority={priority}
			/>
		);
	}

	if (data.type === "VIDEO") {
		return (
			<VideoPlayer
				url={data.url}
				className={className}
				childClassName={childClassName}
				thumbnail={thumbnail}
				disablePlayback={disablePlayback}
				altText={altText}
				priority={priority}
			/>
		);
	}

	const wrapperClass = cn(
		// Subtle fill so lazy-loaded images do not flash as empty white tiles before decode
		"no-media-save relative w-full overflow-hidden bg-muted/40",
		fillTileHeight
			? "flex h-full min-h-0 items-stretch justify-center"
			: "flex aspect-video items-center",
		className,
	);

	const wrapperChild = (
		<GalleryRasterImage
			source={data}
			fallbackRasterSources={fallbackRasterSources}
			listingCard={fillTileHeight}
			preferOriginal={preferOriginalImage}
			alt={altText}
			loading={priority ? "eager" : "lazy"}
			className={cn(
				"size-full min-h-0 min-w-0 object-cover object-center",
				childClassName,
			)}
		/>
	);

	if (altHref !== undefined && isSafeLinkHref(altHref)) {
		return (
			<Link href={altHref} className={wrapperClass} onContextMenu={(event) => event.preventDefault()}>
				{wrapperChild}
			</Link>
		);
	}

	return <div className={wrapperClass} onContextMenu={(event) => event.preventDefault()}>{wrapperChild}</div>;
}

export function VideoPlayer({
	url,
	className,
	childClassName,
	thumbnail,
	disablePlayback,
	altText = "Video content",
	priority = false,
}: Pick<Media, "url"> &
	Pick<Props, "className" | "childClassName" | "thumbnail" | "disablePlayback" | "altText" | "priority">) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const { paused, toggle } = usePlayerControls(videoRef);
	const [showThumbnail, setThumbnailVisibility] = useState(thumbnail !== undefined);
	const playbackUrl = getVideoPlaybackUrl(url) ?? url;
	const posterUrl = thumbnail ? getProxiedImageUrl(thumbnail) ?? thumbnail : undefined;

	const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.stopPropagation();
		e.preventDefault();

		if (showThumbnail) setThumbnailVisibility(false);

		if (paused) {
			ga4VideoPlay({ video_provider: "file", content_type: "media" });
		}
		toggle();
	};

	return (
		<div
			className={cn(
				"no-media-save relative flex aspect-video w-full items-center overflow-hidden bg-muted/40",
				disablePlayback && "pointer-events-none",
				className,
			)}
			onContextMenu={(event) => event.preventDefault()}
		>
		{showThumbnail && thumbnail && (
			<Image
				src={getProxiedImageUrl(thumbnail) ?? thumbnail}
				alt={`${altText} thumbnail`}
				draggable={false}
				width={2048}
				height={1152}
				loading={priority ? "eager" : "lazy"}
				priority={priority}
				quality={100}
				suppressHydrationWarning
				className={cn("size-full absolute inset-0 object-contain", childClassName)}
			/>
		)}

			<video
				ref={videoRef}
				playsInline
				preload="metadata"
				controls={!paused}
				controlsList="nodownload noplaybackrate noremoteplayback"
				disablePictureInPicture
				disableRemotePlayback
				draggable={false}
				poster={posterUrl}
				suppressHydrationWarning
				onContextMenu={(event) => event.preventDefault()}
				className={cn("size-full", disablePlayback && "hidden", childClassName)}
			>
				{!disablePlayback && <source src={`${playbackUrl}#t=0.001`} />}
			</video>

			<div
				className={cn(
					"absolute inset-0 flex flex-col justify-center transition-opacity z-10",
					!paused && "opacity-0 pointer-events-none",
				)}
			>
				<GlassPlayButton
					className="self-center"
					onClick={onClick}
					aria-label={`Play ${altText}`}
				/>
			</div>
		</div>
	);
}

type YouTubePlayerProps = {
	youtubeId: string;
	className?: string;
	disablePlayback?: boolean;
	altText?: string;
	priority?: boolean;
};

export function YouTubePlayer({ youtubeId, className, disablePlayback, altText = "YouTube video", priority = false }: YouTubePlayerProps) {
	const [isPlaying, setIsPlaying] = useState(false);
	const youtubeThumbnail = getYouTubeThumbnail(youtubeId, "maxresdefault");

	const handlePlayClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.stopPropagation();
		e.preventDefault();
		if (!disablePlayback) {
			ga4VideoPlay({ video_provider: "youtube", content_type: "media" });
			setIsPlaying(true);
		}
	};

	return (
		<div
			className={cn(
				"no-media-save relative flex aspect-video w-full items-center overflow-hidden rounded-t-lg bg-muted/40",
				disablePlayback && "pointer-events-none",
				className,
			)}
			onContextMenu={(event) => event.preventDefault()}
		>
			{/* YouTube Thumbnail */}
			<Image
				src={youtubeThumbnail}
				alt={altText}
				draggable={false}
				width={2048}
				height={1152}
				quality={100}
				loading={priority ? "eager" : "lazy"}
				priority={priority}
				suppressHydrationWarning
				className={cn("size-full object-cover object-center rounded-t-lg", isPlaying && "hidden")}
			/>

			{/* YouTube iframe (only when playing) */}
			{isPlaying && !disablePlayback && (
				<iframe
					width="100%"
					height="100%"
					src={`${getYouTubeEmbedUrl(youtubeId)}?autoplay=1`}
					title="YouTube video player"
					frameBorder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowFullScreen
					className="absolute inset-0"
				/>
			)}

			{/* Play Button Overlay */}
			<div
				className={cn(
					"absolute inset-0 flex flex-col justify-center transition-opacity z-10",
					isPlaying && "opacity-0 pointer-events-none",
				)}
			>
				<GlassPlayButton
					className="self-center"
					onClick={handlePlayClick}
					aria-label={`Play ${altText}`}
				/>
			</div>

			{/* Premium Lock Overlay */}
			{disablePlayback && (
				<div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/60 text-white">
					<div className="mb-4 text-6xl">🔒</div>
					<p className="text-lg">Premium Content</p>
				</div>
			)}
		</div>
	);
}
